import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import translate from "@insite/client-framework/Translate";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StoreInvoiceDetailPageTypeLink from "@store/Components/Store/StoreInvoiceDetailPageTypeLink";
import GridItem, { GridItemProps } from "@store/MobiusOverrides/GridItem/GridItem";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { historyTableProps } from "@store/theme/common/props/historyTable";
import { tableGridProps } from "@store/theme/common/props/tableGrid";
import { TYPOGRAPHY } from "@store/theme/design";
import { PaymentModel } from "@store/Types/PaymentHistory";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    payment: PaymentModel | undefined;
}
const mapStateToProps = (state: StoreApplicationState) => ({
    language: state.context.session.language,
});
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface PaymentHistoryInvoicesTableStyles {
    container?: InjectableCss;
    headerClickables?: ClickableProps;
    headerText?: TypographyProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    wrapper?: InjectableCss;
    invoiceCountText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    tableGridHead?: GridContainerProps;
    tableGridBody?: GridContainerProps;
    tableGridRow?: GridItemProps;
    tableGridRowContainer?: GridContainerProps;
    invoiceNumberGridHeader?: GridItemProps;
    invoiceNumberGridCell?: GridItemProps;
    invoiceAmountGridHeader: GridItemProps;
    invoiceAmountGridCell?: GridItemProps;
    dueDateGridHeader?: GridItemProps;
    dueDateBodyGridCell?: GridItemProps;
    mobileHeaderHidden?: HiddenProps;
    mobileHeader?: TypographyPresentationProps;
}

export const invoicesTableStyles: PaymentHistoryInvoicesTableStyles = {
    container: tableGridProps.container,
    centeringWrapper: tableGridProps.centeringWrapper,
    noResultsContainer: historyTableProps.noResultsContainer,
    noResultsText: historyTableProps.noResultsText,
    headerClickables: {
        css: css`
            &:focus {
                outline-style: none;
            }
            width: 100%;
            justify-content: space-between;
            display: block;
        `, // TODO ISC-12604 focus removal is incorrect
    },
    invoiceCountText: historyTableProps.countText,
    headerText: tableGridProps.textHeader,
    invoiceNumberGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 4, 4],
    },
    invoiceNumberGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 6, 6, 4, 4],
        css: css`
            ${tableGridProps.tableGridCell?.css}
            flex-direction: column;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, "flex-start"], properties: ["align-items"] }],
                    mediaOptions.max,
                )}
        `,
    },
    invoiceAmountGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 4, 4],
    },
    invoiceAmountGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 6, 6, 4, 4],
        css: css`
            ${tableGridProps.tableGridCell?.css}
            flex-direction: column;
            align-item: flex-start;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, "end"], properties: ["align-items"] }],
                    mediaOptions.max,
                )}
        `,
    },
    dueDateGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 4, 4],
    },
    dueDateBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 6, 6, 4, 4],
        css: css`
            ${tableGridProps.tableGridCell?.css}
            flex-direction: column;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, "flex-start"], properties: ["align-items"] }],
                    mediaOptions.max,
                )}
        `,
    },
    tableGridHead: tableGridProps.tableGridHead,
    tableGridBody: {
        ...tableGridProps.tableGridBody,
        css: css`
            ${tableGridProps.tableGridBody?.css}
            font-size: ${TYPOGRAPHY.size.smaller};
        `,
    },
    tableGridRowContainer: {
        ...tableGridProps.tableGridRowContainer,
        css: css`
            ${tableGridProps.tableGridRowContainer?.css}
            ${GridOffset} {
                margin: 0;
                align-items: center;
            }
        `,
    },
    tableGridRow: tableGridProps.tableGridRow,
    mobileHeaderHidden: tableGridProps.mobileHeaderHidden,
    mobileHeader: {
        ...tableGridProps.mobileHeader,
        css: css`
            ${tableGridProps.mobileHeader?.css}
            display: block;
        `,
    },
};

const styles = invoicesTableStyles;

const PaymentHistoryInvoicesTable = (props: Props) => {
    const payment = props.payment;

    if (!payment || payment?.invoiceDetails?.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No invoice found")}</Typography>
            </StyledWrapper>
        );
    }

    const invoices = payment.invoiceDetails;
    const rows = invoices.map(invoice => {
        return {
            id: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            invoiceAmount: invoice.invoiceAmount,
            dueDate: invoice.dueDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.dueDate),
                      language: props.language,
                  })
                : "",
        };
    });

    const invoicesCount = invoices.length;

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.invoiceCountText}>
                {invoicesCount} {translate(invoicesCount > 1 ? "Invoices" : "Invoice")}
            </Typography>
            <StyledWrapper {...styles.container}>
                <GridContainer {...styles.tableGridHead}>
                    <GridItem {...styles.invoiceNumberGridHeader}>
                        <Typography {...styles.headerText}>{translate("Invoice Number")}</Typography>
                    </GridItem>
                    <GridItem {...styles.invoiceAmountGridHeader}>
                        <Typography {...styles.headerText}>{translate("Invoice Amount")}</Typography>
                    </GridItem>
                    <GridItem {...styles.dueDateGridHeader}>
                        <Typography {...styles.headerText}>{translate("Due Date")}</Typography>
                    </GridItem>
                </GridContainer>
                <GridContainer {...styles.tableGridBody}>
                    {rows.map(({ id, invoiceNumber, invoiceAmount, dueDate }) => {
                        return (
                            <GridItem {...styles.tableGridRow} key={id} data-test-selector="paymentHistory_invoiceLine">
                                <GridContainer {...styles.tableGridRowContainer}>
                                    <GridItem {...styles.invoiceNumberGridCell}>
                                        <Hidden {...styles.mobileHeaderHidden}>
                                            <Typography {...styles.mobileHeader}>
                                                {translate("Invoice Number")}
                                            </Typography>
                                        </Hidden>
                                        <StoreInvoiceDetailPageTypeLink
                                            title={invoiceNumber}
                                            invoiceNumber={invoiceNumber}
                                            isLink={true}
                                            target="_self"
                                        />
                                    </GridItem>
                                    <GridItem {...styles.invoiceAmountGridCell}>
                                        <Hidden {...styles.mobileHeaderHidden}>
                                            <Typography {...styles.mobileHeader}>
                                                {translate("Invoice Amount")}
                                            </Typography>
                                        </Hidden>
                                        {invoiceAmount && <LocalizedCurrency amount={invoiceAmount} />}
                                    </GridItem>
                                    <GridItem {...styles.dueDateBodyGridCell} data-test-selector="paymentHistoryInvoice_paymentLine_date">
                                        <Hidden {...styles.mobileHeaderHidden}>
                                            <Typography {...styles.mobileHeader}>
                                                {translate("Due Date")}
                                            </Typography>
                                        </Hidden>
                                        <Typography data-test-selector="paymentHistoryInvoice_invoiceLine_date">
                                            {dueDate}
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        );
                    })}
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(PaymentHistoryInvoicesTable);
